'use client'

import { Clock, Flag } from 'lucide-react'
import Image from 'next/image'
import { useEffect, useMemo, useRef, useState, ViewTransition } from 'react'
import { trackEvent } from '@/components/@analitycs/trackEvent'
import { DiamondIcon } from '@/components/diamond/diamond-icon'
import { productImageTransitionName } from '@/features/products-catalog/view-transition'
import { formatCourseWorkloadLabel } from '@/helpers/active-workloads'
import type { GetProductOutput } from '@/http/zamply/products/get-product-schema'
import eMecSeloImg from '@/public/icons/e-MEC-icon.webp'
import starIcon from '@/public/icons/star.svg'
import videoPlayIcon from '@/public/icons/video-play.svg'
import type { CourseType } from '@/types/course-type'
import { getDeviceInfo } from '@/utils/get-device-info'
import { sessionCookie } from '@/utils/session-cookie'

interface HeroProps {
  course: GetProductOutput
  hasMec: boolean
  hasVideoClass: boolean
}

export default function Hero({ course, hasMec, hasVideoClass }: HeroProps) {
  const headerCourseRef = useRef(null)

  const hasTrackedRef = useRef(false)
  const [referrer, setReferrer] = useState('')
  const session = sessionCookie

  const status = session?.state.status
  const fullName = session?.state.profile.full_name
  const email = session?.state.profile.email
  const userId = session?.state.profile.reference
  const externalId = session?.state.profile.external_id
  const device = useMemo(() => getDeviceInfo(), [])
  const workloadLabel = formatCourseWorkloadLabel(course.workloads)

  useEffect(() => {
    setReferrer(document.referrer)
  }, [])

  useEffect(() => {
    if (hasTrackedRef.current || !referrer) return

    const eventDataSelectItem = {
      event: 'vitrine',
      eventName: 'select_item',
      customDataObject: {
        environment: 'Vitrine',
        item_course: course.reference ?? course.external_id,
        item_referrer: referrer,
        item_device: device,
        ...(status === 'authenticated' && {
          user_id: userId ?? externalId,
          item_name: fullName,
          item_email: email,
        }),
      },
    }

    const eventDataViewItem = {
      event: 'vitrine',
      eventName: 'view_item',
      customDataObject: {
        environment: 'Vitrine',
        item_course: course.reference ?? course.external_id,
        item_referrer: referrer,
        item_device: device,
        ...(status === 'authenticated' && {
          user_id: userId ?? externalId,
          item_name: fullName,
          item_email: email,
        }),
      },
    }

    // falta id e area do curso

    const eventDataViewContent = {
      event: 'vitrine',
      eventName: 'ViewContent',
      customDataObject: {
        environment: 'Vitrine',
        content_type: 'product',
        value: course.amount,
        content_name: course.name,
        content_ids: [course.reference ?? course.external_id],
        currency: 'BRL',
        items: {
          sku: course.reference,
          item_id: course.reference ?? course.external_id,
          name: course.name,
          category: course.areas ?? '',
          item_category: course.areas ?? '',
          price: course.amount,
          quantity: 1,
        },
      },
    }

    trackEvent(eventDataSelectItem)
    trackEvent(eventDataViewItem)
    trackEvent(eventDataViewContent)

    hasTrackedRef.current = true
  }, [status, fullName, email, device, referrer, userId, course, externalId])

  const hasShortWorkload =
    course.workloads.filter(workload => Number(workload.workload_value) < 300)
      .length > 0
  const isTypeCourse = course.product_type === 'course'

  // useEffect(() => {
  //   function handleScroll() {
  //     setScroll(window.scrollY);

  //     if (
  //       window.scrollY > headerCourseRef.current?.getBoundingClientRect().height
  //     ) {
  //       setContenCourserScroll(true);
  //     } else {
  //       setContenCourserScroll(false);
  //     }
  //   }

  //   window.addEventListener('scroll', handleScroll);

  //   return () => { };
  // }, [scroll]);

  // useEffect(() => {
  //   function handleScroll() {
  //     setScroll(window.scrollY);

  //     if (
  //       window.scrollY >
  //       cardPriceRef.current?.getBoundingClientRect().height + 500
  //     ) {
  //       setCardPriceScroll(true);
  //     } else {
  //       setCardPriceScroll(false);
  //     }
  //   }

  //   window.addEventListener('scroll', handleScroll);
  // }, [scroll]);
  const coursePremium = false

  return (
    <section
      className="relative flex flex-col items-start justify-between gap-x-6 gap-y-4 pt-20 md:items-start md:pt-14 lg:flex-row"
      ref={headerCourseRef}
    >
      <div className="relative overflow-hidden md:order-1">
        <ViewTransition
          name={productImageTransitionName(
            course.external_id ?? course.reference
          )}
          share="morph"
        >
          <Image
            alt={course.name}
            className="h-80.5 rounded-lg object-cover"
            height={200}
            src={course.image_url ?? ''}
            style={{ objectFit: 'cover' }}
            width={456}
          />
        </ViewTransition>

        {/* Badge Diamond no topo à direita */}
        {coursePremium && (
          <div className="absolute top-3 right-3">
            <div className="flex max-h-5 max-w-16 items-center gap-0.5 rounded-sm border border-[#D4D4D4] bg-[#FAFAFA] p-1 shadow-md">
              <DiamondIcon width={16} />

              <span className="font-medium text-gray-800 text-xss leading-[100%]">
                Diamond
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="max-w-162">
        <div className="md:max-w-162 md:leading-10">
          <span className="font-medium text-base text-primary-700 md:text-lg">
            Curso Online de
          </span>
          <h1 className="font-bold text-2xl text-neutral-900 leading-8 md:text-4.5xl md:leading-11">
            {course.name}
          </h1>

          <p className="mt-4 font-normal text-gray-500 text-sm md:mt-4 md:text-base md:leading-5">
            {course.description}
          </p>
        </div>

        <div className="mt-4 flex items-start justify-between md:justify-start md:gap-4">
          <div className="flex items-center gap-1">
            <Image
              alt="Ícone de estrela"
              height={16}
              src={starIcon}
              width={16}
            />

            <span className="font-normal text-[#656566] text-xss md:text-sm">
              4,5 (1461 alunos)
            </span>
          </div>

          {hasVideoClass && course.has_videoclasses && (
            <div className="flex items-center gap-1">
              <Image
                alt="Ícone de video play"
                height={16}
                src={videoPlayIcon}
                width={16}
              />

              <span className="font-normal text-[#656566] text-xss md:text-sm">
                Vídeos Online
              </span>
            </div>
          )}

          {hasShortWorkload && hasMec && (
            <div className="flex max-w-56 items-start gap-1">
              <Image
                alt="Ícone de arquivos"
                height={16}
                src={eMecSeloImg}
                width={16}
              />

              <span className="font-normal text-[#656566] text-xss md:text-sm">
                Certificado emitido por instituição credenciada ao MEC
              </span>
            </div>
          )}

          {workloadLabel && (
            <div className="flex justify-start gap-px">
              <Clock className="size-5 text-gray-50" fill="#D1A5FF" />

              <div className="leading-none">
                <span className="font-semibold text-[#656566] text-xss md:text-sm">
                  Carga horária
                </span>
                <small className="block font-medium text-[#ABACAD] text-xs">
                  {workloadLabel}
                </small>
              </div>
            </div>
          )}
        </div>

        <div className="text-sm">
          <div className="flex items-start gap-1">
            <Flag className="text-[#D1A5FF]" />

            <div>
              <strong className="text-[#66666]">Modalidade do Curso</strong>
              <div>
                {hasShortWorkload && hasMec ? (
                  <span
                    className={`text-gray-500 ${isTypeCourse ? '' : 'hidden'}`}
                  >
                    Curso online de Capacitação, Atualização e Extensão ou
                    Aperfeiçoamento <br />
                    (a depender da carga horária)
                  </span>
                ) : (
                  <span className="text-gray-500">
                    Curso online de Aperfeiçoamento <br />
                    Atualização e Capacitação.
                  </span>
                )}
                <span
                  className={`text-gray-500 ${isTypeCourse ? 'hidden' : ''}`}
                >
                  Webinar.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
